import {
    Api,
    ModelRow,
    Navigation,
    StringUtil,
} from "@mcleod/core";
import {
    BlurEvent,
    Button, Checkbox, ClickEvent, DataSourceMode, Layout, LookupModelSelectionEvent, SelectionType, SlideoutDecorator, TableRow,
    TableRowDisplayEvent,
    TableSelectionEvent,
    Textbox
} from "@mcleod/components";
import { AutogenLayoutProspectToLocation } from "./autogen/AutogenLayoutProspectToLocation";

const viewLocationURL = "lme/ar/Location?mode=update&key=";

export class ProspectToLocation extends AutogenLayoutProspectToLocation {
    assignSelectedButton: Button;

    static async showAsSlideout(rows: ModelRow[], buttonAssign: Button) {
        const saveButton = new Button({
            id: "buttonAssignLocation",
            caption: "Convert",
            onClick: async (event: ClickEvent) => {
                if (!layout.mainDataSource.validateSimple()) {
                    return;
                }
                saveButton.busy = true;
                const rowsToPost = layout.tableProspectsCovert.selectedRows.map(row => {
                    return {
                        ...row.data.data
                    };
                });
                const location_type_id = layout.mainDataSource.activeRow.get("location_type_id");
                const operations_user = layout.mainDataSource.activeRow.get("operations_user");
                const start_date = layout.mainDataSource.activeRow.get("start_date");
                const location_code = layout.mainDataSource.activeRow.get("location_code");
                const location_status_id = layout.mainDataSource.activeRow.get("location_status_id");
                const response = await Api.post("lme/crm/convert-prospect-to-location", {
                    prospects: rowsToPost,
                    location_type_id, operations_user, start_date, location_code, location_status_id
                });
                const locationIds = [];
                response.data.forEach((row) => {
                    const locationId = row.id;
                    if (locationId) {
                        locationIds.push(locationId);
                    }
                });
                if (locationIds.length > 0)
                    Navigation.navigateTo("lme/dispatch/LocationList?ids=" + locationIds.toString(), { newTab: false });
                saveButton.busy = false;
            },
            backgroundColor: "primary",
            color: "primary.reverse",
            minWidth: 128,
            borderWidth: 0,
            rowBreak: false
        });
        const layout = Layout.getLayout("lme/crm/ProspectToLocation") as ProspectToLocation;
        layout.assignSelectedButton = saveButton;
        const prospects = [];
        for (const row of rows) {
            layout.setLookupModelDisplayValues(row);
            prospects.push({
                ...row.data
            });
        }
        buttonAssign.busy = false;
        return new SlideoutDecorator({
            title: "Prospect to Location Conversion",
            layout: layout,
            width: "85%",
            fillVerticalSpace: true,
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
                await layout.sourcePreviewProspectToLocation.search({ prospects });
                layout.updateTitleNumbers();
            },
            onClose: async (cancelled: boolean) => {
                layout.slideOut();
            },
            addlComponents: [saveButton]
        });
    }

    tableProspectsConvertOnRowDisplay(event: TableRowDisplayEvent) {
    const tableRow = event.getTableRow();
    const modelRow = tableRow.data as ModelRow;
    const textboxNewProspectID = tableRow.findComponentById("textboxNewProspectID") as Textbox;
    const checkbox = tableRow.findComponentById("checkboxSelectRow") as Checkbox;
    tableRow.table.selectRow(tableRow, SelectionType.DISTINCT, null);
    if (textboxNewProspectID != null) {
        // Track validation states
        let maxLengthValid = true;
        let duplicateCheckValid = true;
        
        const updateRowState = () => {
            const isValid = maxLengthValid && duplicateCheckValid;
            if (isValid) {
                const alreadyUnselectedEnabled = !tableRow.selected && tableRow.enabled;
                checkbox.enabled = true;
                tableRow.enabled = true;
                if (!tableRow.selected && !alreadyUnselectedEnabled) {
                    tableRow.table.selectRow(tableRow, SelectionType.DISTINCT, null);
                }
            }
            else {
                checkbox.enabled = false;
                tableRow.enabled = false;
                if (tableRow.selected) {
                    tableRow.table.selectRow(tableRow, SelectionType.DISTINCT, null);
                }
            }
            this.updateTitleNumbers();
        };

        // Combined blur listener for both validations
        textboxNewProspectID.addBlurListener(async (event: BlurEvent) => {
            const idToValidate = textboxNewProspectID.getDataValue() || "";
            let validationMessage = "";
            
            // Check max length
            maxLengthValid = idToValidate.length <= 8;
            if (!maxLengthValid) {
                validationMessage = "Maximum length is 8 characters.";
            }
            
            // Check for duplicates if max length is valid and value is not empty
            if (maxLengthValid && !StringUtil.isEmptyString(idToValidate)) {
                try {
                    const response = await Api.post("lme/dispatch/check-duplicate-code", {
                        "id": idToValidate,
                        "table": "location"
                    });
                    
                    if (response.data[0]?.is_duplicate) {
                        duplicateCheckValid = false;
                        validationMessage = `The code [ ${idToValidate} ] is a duplicate of an existing location. Please enter a new one.`;
                    } else {
                        duplicateCheckValid = true;
                    }
                } catch (error) {
                    console.error("Error checking duplicate customer ID:", error);
                    duplicateCheckValid = false;
                    validationMessage = "Error validating ID. Please try again.";
                }
            } else if (StringUtil.isEmptyString(idToValidate)) {
                duplicateCheckValid = true;
            }
            
            // Set validation state and styling
            const isValid = maxLengthValid && duplicateCheckValid;
            if (!isValid) {
                textboxNewProspectID.validationWarning = validationMessage;
                textboxNewProspectID.borderColor = "error";
                textboxNewProspectID.showAdhocTooltip(null, { timeout: 5000 });
            } else {
                textboxNewProspectID.validationWarning = null;
                textboxNewProspectID.borderColor = null;
            }
            
            updateRowState();
        });
    }

    const potentialMatches = modelRow.get("potential_matches");
    const textboxProspectToLink = tableRow.findComponentById("textboxProspectToLink") as Textbox;
    if (textboxProspectToLink != null) {
        if (potentialMatches != null && potentialMatches.length > 0) {
            const imageWarning = tableRow.findComponentById("imageWarning");
            if (imageWarning != null) {
                imageWarning.visible = true;
            }
            textboxProspectToLink.addBeforeLookupModelSearchListener((event) => {
                // Safely encode each ID to avoid delimiter collision
                const idFilter = potentialMatches.map(match => encodeURIComponent(match.id)).join(" || ");
                event.filter.near_ids = idFilter;
            });
        }
        else {
            textboxProspectToLink.visible = false;
        }
    }
}

    textboxOperationsUserBeforeLookupModelSearch(event) {
        event.filter.web_user_type = "U || E";
    }

    tableProspectsConvertOnSelect(event: TableSelectionEvent) {
        this.updateTitleNumbers();
    }

    updateTitleNumbers() {
        this.labelRecordCount.text = this.tableProspectsCovert.selectedRows.length.toString();
        if (this.tableProspectsCovert.selectedRows.length == 0) {
            this.assignSelectedButton.enabled = false;
            this.assignSelectedButton.disabledTooltip = "You must select at least one prospect to assign.";
        }
        else {
            this.assignSelectedButton.enabled = true;
        }
    }

    setLookupModelDisplayValues(row: ModelRow) {
        row.set("def_commodity_id_display", row.getFirstLookupModelData("def_commodity_id")?.get("descr"));
        row.set("category_display", row.getFirstLookupModelData("category")?.get("descr"));
    }

    textboxProspectToLinkOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textboxProspectToLink = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textboxProspectToLink);
        const textboxNewProspectID = tableRow.findComponentById("textboxNewProspectID") as Textbox;
        if (textboxNewProspectID) {
            if (event.hasSelection() !== true) {
                textboxNewProspectID.enabled = true;    
            }
            else {
                textboxNewProspectID.enabled = false;
                textboxNewProspectID.clear();
                textboxNewProspectID.disabledTooltip = "When a location is selected to link, a new location will not be created so this field is disabled.";
            }
        }   
    }

    textboxNewProspectOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        if (textbox) {
            const tableRow = TableRow.getContainingTableRow(textbox);
            const textboxProspectToLink = tableRow.findComponentById("textboxProspectToLink") as Textbox;
            const value = textbox.getDataValue();
            if (!StringUtil.isEmptyString(value)) {
                textboxProspectToLink.clear();
                textboxProspectToLink.enabled = false;
                textboxProspectToLink.disabledTooltip = "You may not link a location when a new location ID to create is entered.";
            }
            else {
                textboxProspectToLink.enabled = true;
            }
        }
    }
}